import React, { useMemo, useState, useRef, useEffect } from "react";
import { motion, useInView } from "framer-motion";

// Helper hook to detect mobile screens
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return isMobile;
}

// Координати за градовете в координатна система на viewBox="0 0 800 500"
const CITY_POS = {
  София: { x: 180, y: 300 },
  Пловдив: { x: 360, y: 370 },
  "Стара Загора": { x: 460, y: 300 },
  Бургас: { x: 640, y: 340 },
  Варна: { x: 690, y: 220 },
  Добрич: { x: 660, y: 170 },
  Шумен: { x: 570, y: 210 },
  Русе: { x: 450, y: 130 },
  "Велико Търново": { x: 420, y: 230 },
  Габрово: { x: 400, y: 250 },
  Плевен: { x: 320, y: 210 },
  Монтана: { x: 170, y: 200 },
  Благоевград: { x: 160, y: 380 },
  Смолян: { x: 360, y: 450 },
  Хасково: { x: 480, y: 380 },
};

// Helpers for calendar actions
const pad2 = (n) => String(n).padStart(2, "0");
const toICSDate = (y, m, d) => `${y}${pad2(m)}${pad2(d)}`; // YYYYMMDD

// Parses strings like: "6-8.09", "30.09-01.10", "03-04.10"
function parseDateRangeBG(dateStr, defaultYear = 2025) {
  // Normalize: trim, remove inner spaces, unify dash types (hyphen, en dash, em dash)
  const s = String(dateStr).trim().replace(/\s+/g, "").replace(/[–—]/g, "-");
  // Split by '-'
  const parts = s.split("-");
  if (parts.length !== 2) return null;
  const left = parts[0].trim();
  const right = parts[1].trim();

  // Extract day/month where available
  const parseDM = (frag) => {
    const dm = frag
      .split(".")
      .map((x) => x.trim())
      .filter(Boolean);
    if (dm.length === 0) return { d: NaN, m: null };
    if (dm.length === 1) {
      return { d: parseInt(dm[0], 10), m: null };
    }
    return { d: parseInt(dm[0], 10), m: parseInt(dm[1], 10) };
  };

  const L = parseDM(left);
  const R = parseDM(right);

  // Infer missing month on the left from the right side
  const monthL = L.m ?? R.m;
  const monthR = R.m ?? L.m;
  if (!monthL || !monthR || !L.d || !R.d) return null;

  // Build start/end (DTEND in ICS is exclusive → add +1 day to the last day)
  let yStart = defaultYear;
  let yEnd = defaultYear;
  // If month wraps from e.g. 09 → 10 with a smaller day number, it's still same year range
  // (Adjust here if your schedule ever crosses New Year.)

  const start = { y: yStart, m: monthL, d: L.d };
  const endInc = { y: yEnd, m: monthR, d: R.d }; // inclusive end

  // Compute exclusive end = endInc + 1 day
  const endDateObj = new Date(Date.UTC(endInc.y, endInc.m - 1, endInc.d));
  endDateObj.setUTCDate(endDateObj.getUTCDate() + 1);
  const endExclusive = {
    y: endDateObj.getUTCFullYear(),
    m: endDateObj.getUTCMonth() + 1,
    d: endDateObj.getUTCDate(),
  };

  return {
    dtStart: toICSDate(start.y, start.m, start.d),
    dtEndExclusive: toICSDate(endExclusive.y, endExclusive.m, endExclusive.d),
  };
}

function buildICS({ title, description, location, dtStart, dtEndExclusive }) {
  const now = new Date();
  const dtStamp = `${now.getUTCFullYear()}${pad2(now.getUTCMonth() + 1)}${pad2(
    now.getUTCDate()
  )}T${pad2(now.getUTCHours())}${pad2(now.getUTCMinutes())}${pad2(
    now.getUTCSeconds()
  )}Z`;
  const uid = `evroadshow-${Date.now()}@saleford`;
  const escape = (v) =>
    String(v || "")
      .replace(/\\n/g, "\\n")
      .replace(/,/g, "\\,")
      .replace(/;/g, "\\;");
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Sale Ford//EV Roadshow//BG",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${dtStamp}`,
    `SUMMARY:${escape(title)}`,
    description ? `DESCRIPTION:${escape(description)}` : null,
    location ? `LOCATION:${escape(location)}` : null,
    `DTSTART;VALUE=DATE:${dtStart}`,
    `DTEND;VALUE=DATE:${dtEndExclusive}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ]
    .filter(Boolean)
    .join("\r\n");
}

function downloadICS(filename, icsText) {
  const blob = new Blob([icsText], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function isoToICSRange(startIso, endIso) {
  if (!startIso || !endIso) return null;
  const s = new Date(startIso);
  const e = new Date(endIso);
  if (isNaN(s) || isNaN(e)) return null;
  const dtStart = toICSDate(s.getFullYear(), s.getMonth() + 1, s.getDate());
  const endExclusiveObj = new Date(e);
  endExclusiveObj.setDate(endExclusiveObj.getDate() + 1);
  const dtEndExclusive = toICSDate(
    endExclusiveObj.getFullYear(),
    endExclusiveObj.getMonth() + 1,
    endExclusiveObj.getDate()
  );
  return { dtStart, dtEndExclusive };
}

function fmtDM(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}.${mm}`;
}

function buildGoogleCalUrl({
  title,
  details,
  location,
  dtStart,
  dtEndExclusive,
}) {
  const base = "https://calendar.google.com/calendar/render?action=TEMPLATE";
  const params = new URLSearchParams({
    text: title,
    details: details || "",
    location: location || "",
    dates: `${dtStart}/${dtEndExclusive}`, // all-day format
  });
  return `${base}&${params.toString()}`;
}

export default function EVRouteMap({ schedule, onOpenContact }) {
  const openContact =
    typeof onOpenContact === "function" ? onOpenContact : () => {};
  const [selectedIdx, setSelectedIdx] = useState(null);
  const isMobile = useIsMobile();

  const rootRef = useRef(null);
  const inView = useInView(rootRef, {
    once: true,
    margin: "-20% 0px -20% 0px",
  });

  const handleToggle = (idx) => {
    setSelectedIdx((prev) => (prev === idx ? null : idx));
  };

  return (
    <div
      ref={rootRef}
      className="relative w-full py-0 pb-12 mx-auto overflow-visible"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Escape") setSelectedIdx(null);
      }}
    >
      <svg
        viewBox="0 0 800 500"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="xMidYMin meet"
        className="block w-full h-auto text-fordLight"
        role="img"
        aria-label="Карта на България с маршрут на EV Roadshow"
      >
        {/* Фон: реален SVG контур (поставен в public/images/roadshowev/bg-01.svg) */}
        <defs>
          <filter id="popupShadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow
              dx="0"
              dy="2"
              stdDeviation="4"
              floodColor="rgba(0,0,0,0.2)"
            />
          </filter>
          <filter id="glassShadow" x="-30%" y="-30%" width="160%" height="160%">
            <feDropShadow
              dx="0"
              dy="1"
              stdDeviation="2"
              floodColor="rgba(0,0,0,0.25)"
            />
          </filter>
          {/* SVG blur filter for ambient circles */}
          <filter id="bgBlur" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="60" />
          </filter>
          {/* Add linear gradient for background */}
          <linearGradient id="bgGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#102B4E" />
            <stop offset="70%" stopColor="#102B4E" />
            <stop offset="100%" stopColor="#2861A4" />
          </linearGradient>
          {/* Subtle text shadow for city labels */}
          <filter id="labelShadow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow
              dx="0"
              dy="1"
              stdDeviation="0.6"
              floodColor="rgba(0,0,0,0.35)"
            />
          </filter>
        </defs>

        {/* Gradient background instead of blur circles */}
        <rect width="800" height="500" fill="url(#bgGradient)" />

        <g transform="translate(0,-36)">
          {/* Контур на България (inline), над градиента */}
          <path
            d="M501.577,465.807l-4.546,0.43l-2.481-0.724l-1.527-1.293l-3.141-3.607l-0.978-0.61l-1.699-0.127l-2.591,2.502
			l-6.477,0.205l-3.165,0.872l-3.177,2.595l-0.88,0.377l-0.807,0.045l-0.794,0.246l-0.807,0.917l-0.66,1.968l-0.073,2.291l0.33,2.241
			l0.562,1.852l0.527,0.787l0.878,1.31l1.686,0.797l1.662,0.327l1.357,0.65l1.014,1.21l0.684,1.332l1.088,4.036l0.086,1.193
			l-0.183,2.491l0.208,1.061l0.66,0.547l1.858,0.343l0.66,0.743l0.599,2.081l0.159,1.804l-0.183,1.779l-0.428,1.999l-1.381,1.427
			l-0.208,1.06l1.222,2.781l0.501,0.717l1.638,1.557l0.367,0.954l-0.208,1.271l-0.599,0.367l-0.745,0.269l-0.623,0.953l-1.32,3.927
			l-1.821,3.999l-1.369,2.165l-0.733,0.407l-0.868-0.22l-10.106,2.352l-1.528,0.765l-3.153,2.132l-2.676,1.302l-1.307,0.163
			l-1.589-0.309l-1.65-0.016l-2.945,1.537l-1.686,0.35l-2.285-0.952l-3.471-3.865l-2.64-1.009l-1.21,0.472l-4.412,2.994l-4.106,0.309
			l-1.332,0.464l-1.454,1.074l-0.843,0.846l-0.806,0.553l-1.332,0.195l-1.124-0.317l-2.469-1.464l-1.161-0.407l-10.436-0.724
			l-1.601,0.545l-0.917,1.489l-0.77,1.74l-1.039,1.293l-1.161,0.455l-0.917-0.098l-0.929-0.293l-1.228-0.114l-3.031,0.529
			l-19.834,6.454l-2.774,0.203l-2.737-0.439l-2.297-1.39l-5.035-6.943l-2.273-1.928l-0.476-0.224l-4.412-2.078l-0.464-0.537
			l-0.917-1.562l-0.354-0.407l-0.599-0.008l-1.943,0.415l-19.32-8.238l-3.617-2.248l-1.589-0.195l-1.173,0.244l-2.468,0.88
			l-4.595,0.326l-0.367,2.101l0.318,2.744l-1.014,2.287l-2.346-0.106l-2.578-2.312l-4.008-5.195l-2.31-2.085l-2.163-1.035
			l-2.322-0.342h-2.725l0.721-1.573l-4.118,0.065l-1.613-0.399l-1.821-1.695l-0.086-0.579l0.293-1.744l-0.134-0.783l-0.452-0.53
			l-1.124-0.718l-0.428-0.465l-0.635-0.913l-0.611-0.62l-0.489-0.734l-0.342-1.297l-0.379-4.373l-1.466-4.138l-2.456-2.229
			l-3.41,1.331l-2.615,0.588l-2.408,3.445l-1.76,0.392l-2.493-0.416l-1.821,0.188l-3.972,1.191l-1.051,0.114l-3.165-0.375
			l-1.027,0.155l-0.77,0.449l-0.819,0.269l-1.1-0.375l-0.208-0.873l0.269-1.339l-0.11-1.38l-2.31-1.486l-1.906-2.164l-2.065,0.261
			l-2.236,0.955l-1.971,1.272l-0.168,0.108l-1.735,1.527l-0.489,1.004l-0.513,2.481l-0.342,0.588l-0.88-0.074l-0.513-0.677
			l-0.525-0.914l-0.917-0.735l-3.519-1.045l-4.717-0.416l-3.458,1.624l0.293,5.067l0.318,3.499l-0.061,1.337l-0.721,1.19l-1.32,0.652
			l-1.405-0.277l-2.615-1.745l-0.428,1.125l-0.562,0.375l-0.648,0.147l-0.66,0.448l-1.967,1.973l-1.747,0.122l-5.56-4.092
			l-0.953-0.13l-3.202,2.976l-1.906,0.929l-2.444,0.627l-2.505,0.285l-2.09-0.106l-1.772,0.856l-2.676,4.473l-1.919,0.505
			l-3.886-0.896l-3.923,0.033l-2.285,0.839l-2.97,3.013l-0.342,0.223l-1.467,0.958l-3.959-0.155l-7.797-4.056l-11.646-0.342
			l-2.249,0.733l-3.593,2.631l-2.114,1.156l-2.505,0.301l-1.234-1.189l-1.112-1.816l-2.188-1.547l-2.102,0.147l-2.762,1.189
			l-2.64,1.686l-1.711,1.645l-0.403,1.205l-0.183,2.873l-0.623,1.53l-1.051,1.09l-1.295,0.74l-2.664,0.789l-4.961,0.578
			l-23.439-3.637l2.823-2.214l0.428-2.931l-0.599-3.306l-0.244-3.364l0.379-0.969l1.369-1.703l0.293-0.742l-0.147-0.913l-0.684-1.606
			l-0.159-0.766l-0.311-11.044l-0.068-2.424l0.574-2.621l0.098-0.923l-0.269-1.07l-1.124-1.764l-0.379-0.898l-0.196-2.112l0.11-2.276
			l0.367-2.186l0.562-1.815l0.587-0.544l1.857-0.54l0.648-0.405l0.367-0.789l0.77-2.298l1.043-1.685l1.56-2.52l1.295-3.683
			l-0.086-3.733l-2.09-3.337l-1.259-0.594l-2.799-0.147l-1.295-0.582l-0.733-1.168l-2.53-5.971l-1.259-5.433l-0.745-1.879l-0.55-0.64
			l-1.381-0.747l-0.349-0.392l-0.152-0.17l-0.306-1.202l0.049-2.343l-0.183-1.116l-1.271-3.629l-0.88-3.655l-0.208-3.817l-0.281-1.71
			l-0.929-1.726l-0.037-0.025l-0.098-2.105l-0.017-0.086l-0.227-1.123l-0.648-0.798l-1.332-0.892l-0.672-0.045l-1.809,0.633
			l-0.831,0.025l-0.929-0.728l-0.403-1.078l-0.281-1.045l-0.525-0.642l-1.21-0.132l-5.389,0.243l-0.782-0.317l-0.525-0.662
			l-0.44-0.695l-0.538-0.469l-3.532-0.745l-5.756-2.949l-1.112-0.57l-10.253-7.396l-2.469-2.501l-0.383-0.659l-1.45-2.493
			l-1.564-4.589l-4.473-3.37L30,370.258l2.261-1.207l4.925-0.091l2.151-0.694l1.711-2.265l1.882-5.875l1.821-2.391l1.809-0.873
			l1.479-0.327l1.332-0.918l1.259-2.561l1.613-5.874l0.403-3.334l-0.477-2.407v-0.025l-0.904-2.263l-1.503-1.848l-3.629-2.637
			l-6.122-5.744l-0.513-0.232l0.416-3.199l1.475-6.445l0.382-1.671l0.55-3.967l-0.843-2.207l3.08-5.895l1.576-1.505l0.134-0.482
			l0.037-0.499l-0.037-0.491l-0.134-0.482l-1.76-1.402l-1.589-2.426l-2.811-6.852l-0.416-0.6l0.183-0.608l0.361-0.558l0.751-1.162
			l1.027-0.95l3.006-1.604l1.247-1.067l1.845-2.851l1.063-0.921l1.625-0.038l2.102,0.325l0.819-0.492l0.034-0.051l0.613-0.912
			l1.613-1.117l3.226-0.421l8.909,2.414l3.593-0.884l3.593-1.53l1.393-1.693l0.092-0.238l0.702-1.81l2.09-7.845l0.733-2.042
			l0.831-1.365l1.381-0.835l3.275-0.777l1.54-0.606h0.012l0.012-0.008v-0.025l1.564-2.239l4.962-4.714l0.623-1.25l0.855-2.969
			l0.587-1.137l1.002-0.895l2.004-0.95l0.965-0.715l2.407-3.678l2.175-5.333l1.246-5.419l-0.197-2.058l-0.182-1.893l-1.967-0.88
			l-5.817-1.27l-2.102-1.312l-1.65-1.665l-3.128-4.278l-2.86-2.874l-0.733-1.095l-0.367-1.284l-0.367-2.964l-0.428-1.293
			l-1.491-2.192l-8.457-8.542l-1.025-0.74l-0.588-0.425l-1.943-0.874l-1.149-0.135l-2.249,0.118l-1.124-0.42l-0.682-0.743
			l-0.284-0.309l-1.32-2.784l-0.892-0.988l-1.1-0.307l-2.554,0.139l-1.259-0.286l-1.124-0.858l-1.662-2.234l-0.819-0.846
			l-3.923-1.873l-1.625-1.532l-1.124-3.112l-2.212-7.752l-1.442-2.993l-0.098-0.805l0.122-0.818l0.33-0.805l0.147-0.422l0.049-0.422
			l-0.049-0.401l-0.147-0.392l-0.391-0.561l-0.122-0.607l0.122-0.591l0.391-0.59l0.037-0.067l0.025-0.084l-0.025-0.084l-0.037-0.11
			l-0.904-2.017l-0.11-3.748l-0.782-2.153l-1.222-1.191l-3.483-1.93l-1.479-1.381l-1.075-1.719l-0.941-1.999l-0.758-2.134
			l-0.525-2.135l-0.025-1.361l0.44-2.732l-0.122-1.273l-3.067-3.693l-1.552-4.436l0.623-3.569l1.515-3.778l1.124-5.047l0.232-4.918
			l0.354-0.831l1.173-2.185l0.306-0.734l0.269-2.563l0.061-4.763l0.269-2.038l1.442-2.234l2.664-1.155l3.732-0.603l1.84-0.297
			l2.627-0.076l1.332-1.708l0.904-2.371l1.369-2.049l2.42-0.867l2.432,0.17l2.102-0.417l1.393-2.543l-0.012-1.446l-0.77-3.48
			l-0.122-1.609l0.232-1.856l1.222-4.879l-0.171-0.009l-0.232-0.519l-0.134-0.877l0.159-1.005l0.281-0.213l1.919-2.232l1.796-2.957
			l1.039-1.1l5.068-2.382l25.397,17.4l4.277,1.881l5.377,0.732l2.391,1.083l2.68,1.214l1.075,5.061l-2.016,5.05l-4.13,2.294
			l-2.579,0.348l-4.705,1.546l-2.444,0.357l-2.359,1.554l-1.32,3.694l-2.86,12.324l0.061,3.713l1.466,3.106l2.994,2.605l3.642,0.868
			l15.725-1.409l9.376-0.84l3.556-1.538l4.143-0.89l4.436-2.386l10.721-1.525l18.864,0.982l12.77,7.08l3.329,0.56l1.804,0.304
			l10.008-2.241l2.615,0.513l6.746,3.972l41.346,10.44l1.156,0.292l20.967-1.033l2.546-0.126l2.041-0.63l6.587-4.963l4.155-1.35
			l3.996,0.478l19.111,7.179l5.184,1.946l5.56,0.812l24.954-1.768l13.943,5.019l15.337,1.154l4.851,1.183l3.91,2.331l0.392,0.234
			l4.057,3.147l4.326,2.521l5.145,0.693l2.713-0.709l4.888-2.183l1.778-0.321l0.746-0.135l5.328,0.304l2.688-0.275l2.187-1.149
			l2.224-0.536l2.676-1.158l2.53-1.559l1.845-1.775l2.114-1.509l7.279-0.233l0.786-0.025l4.913-2.139l2.75-4.567l0.22-0.613
			l3.91-4.058l3.544-2.04l5.573-7.127l0.953-2.334l1.124-1.92l14.224-10.503l0.868-2.537l2.09-3.166l4.363-4.9l4.082-2.226
			l9.544-2.481l9.348-4.131l2.58-0.381l9.868-1.458l23.456-3.467l3.972-1.404l2.371-0.281l1.124-0.323l2.352-1.489l1.356-0.332h5.322
			l4.228-1.294L566.7,71.1l13.882-1.414l3.067-1.968l8.603,5.366l12.453,2.512L607.21,77l2.884-0.272l0.181,0.141l1.933,1.502
			l-0.556,3.727l2.487,2.849l6.66,3.189l1.405,1.284l2.285,4.037l1.295,0.926l22.547-0.196l6.966-2.218l2.701,0.969l2.346,4.987
			l0.756,1.033l4.633,6.328l7.76-1.918l8.175-4.636l6.575-0.773v0.017l2.811,4.738l5.34,18.883l3.996,3.164l24.41,11.148
			l25.223,4.382l17.003-0.993l-0.26,2.251l-0.366,19.84l0.289,2.093l1.203,2.856l1.203,1.979l0.808,1.793l0.096,2.376l-1.058,4.18
			l-1.905,5.173l-2.146,4.388l-1.79,1.842l-1.203,0.636l-5.485,5.24l-1.107,1.569l-0.635,2.039l-0.087,2.833l-1.617-2.25
			l-5.312-2.959l-1.135-0.947l-1.318-1.563l-2.973-1.219l-5.562-1.06l-2.656,0.53l-4.445,2.292l-2.175,0.517l-7.707-0.358
			l-2.454,0.358l-4.542,2.642l-3.224,4.488l-0.539,1.416l-6.611,17.268l-1.597,2.635l-2.059,1.545l-8.063,2.178l-1.347,0.931
			l-0.799,0.277h-1.164v1.109l4.109,3.081l0.808,1.24l-0.144,1.9l-2.271,7.054l-1.626,7.84l-0.837,1.508l-1.039,1.067l-0.924,1.152
			l-0.491,1.803l0.221,3.619l1.771,7.392l0.423,3.326l-0.654,10.917l0.654,2.416l-2.367,1.824l-0.048,2.651l0.953,3.477l0.654,4.249
			v7.693l-0.289,3.019l-0.289,1.12l-6.052,0.354l-6.351-1.1l-5.225-0.144l-1.241,0.249l-0.837,0.969l-0.779,2.239l-0.375,1.741
			l0.135,1.485l0.847,1.211l1.819,0.949l-3.166,0.549l-5.148,2.244l-2.656,0.504l-2.165,1.864l0.231,4.217l1.309,4.489l1.03,2.685
			l-1.905-0.908l-11.018-0.288l-3.474,1.999l-1.53,3.304l-0.837,3.962l-1.338,3.987l-2.117-1.07l-1.107,1.514l0.058,2.387
			l1.453,1.539l-0.423,1.108l-0.115,0.828l0.539,2.445l0.327-1.199l0.173-0.209l-0.058-0.02l-0.443-0.665l0.828-3.286l3.695,3.932
			l1.212,0.352l1.443-1.369l0.953-1.858l1.184-0.763l2.078,1.898l1.068-1.65l1.01,0.202l0.962,0.919l0.924,0.528l1.665-0.476
			l1.164-0.756l1.174-0.444l1.645,0.483l-0.058,0.822l-0.125,0.156l-0.231-0.039l-0.395,0.254l0.702,2.771l1.155,1.981l1.838,0.391
			l2.829-1.955l0.221,1.506l0.654,0.802l1.039,0.182l1.318-0.313v1.199l-0.683,0.202l-1.799,0.893l0.876,1.564l0.5,0.397l1.107,0.228
			l-1.126,1.589l-0.135,2.188l0.712,2.227l1.357,1.64l1.722,0.742l1.386-0.176l1.463-0.469l1.886-0.098v0.996l-0.693,0.879
			l-0.019,0.879l0.558,0.865l1.03,0.852l-2.944,2.459l-1.395,2.966l0.558,2.965l2.906,2.418l-0.52,0.429l-0.048,0.163l-0.231,0.598
			l4.263,2.794l0.654,1.026l0.558,1.169l1.347,1.539l1.597,1.422l1.337,0.766l-0.241,0.539l-0.327,1.246l-0.241,0.487l3.512,2.044
			l0.904,1.097l2.04,4.392l6.36,4.066l0.779,1.543l0.981,1.471l1.645,0.525l-0.491,1.75l0.577,1.49l1.135,1.231l1.193,0.972
			l0.067,0.797l-0.173,0.214l-0.318,0.013l-0.385,0.162l1.347,1.444l0.885,2.059l0.154,2.208l-0.77,1.897l0.346,1.605l-4.229-0.948
			l-1.784-0.575l-5.78,0.674l-1.613-0.501l-3.128-1.537l-2.921-0.752l-1.088-0.049l-2.249,0.362l-0.483-0.193l-0.471-0.07
			l-0.477,0.07l-0.464,0.193l-0.361,1.599l-0.006,2.153l-0.257,1.565l1.839,1.142l-0.348,0.965l-1.479,0.591l-1.54,0.062
			l-1.613-0.546l-6.171-3.414l-4.363-0.16l-9.196,2.404l-0.287,1.586l-0.415,0.776l-0.623,0.004l-0.892-0.682l-0.965,1.273
			l-1.204,0.801l-2.536,0.895l0.159,0.378l0.33,0.238l0.36,0.201l0.324,0.222l-0.379,0.345l-0.733,0.382l-0.281,0.353l1.638,0.706
			l-1.931,0.837l-1.564-1.096l-2.805-3.991l-1.79-1.536l-8.689-4.909l-2.835-2.482l-2.603-3.083l-5.01-7.757l-3.214-3.21
			l-3.776-2.256l-4.155-0.984l-2.579,0.366l-1.491,1.194l-2.627,3.544l-0.306,0.638l-0.098,0.56l-0.513,0.206l-2.866-0.757
			l-2.646-0.119l-1.32,0.119l-1.821-1.477l-2.004-1.165l-2.151-0.683l-2.082-0.034l-0.161-0.003l-2.829,1.441l-2.627,4.95
			l-2.285,1.584l-1.644,0.695l-1.057,1.633l-0.965,1.92l-1.32,1.583l-1.026,0.271l-2.218-0.362l-1.228,0.526l-1.271,1.282
			l-1.088-0.017l-1.112-0.415l-1.295,0.037l-1.271,0.801l-1.433,1.216l-1.207,1.024l-0.978,0.497l-1.301-0.353l-2.188-1.919
			l-1.106-0.555l-1.418,0.423l-2.554,2.589l-1.21,0.879l-2.267,0.263l-11.114-1.865l-2.114,0.259l-1.919,1.376l-3.409,3.668
			l-0.868,0.653l-0.733,0.743l-0.489,1.039l0.208,0.624l1.43,0.903l0.354,0.542l-0.098,0.891l-0.501,1.396l-0.134,0.833l-0.084,0.962
			l-0.099,1.143l-0.501,2.884l-0.978,2.904l-1.589,2.181l-5.646,1.738l-12.159-0.529l-4.888,4.295l-1.674,3.818l-0.44,3.44
			l0.819,3.181L501.577,465.807z"
            fill="none"
            stroke="#2861A4"
            strokeWidth="1.2"
            strokeOpacity="0.85"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Маркери за градовете — появяват се, когато линията ги достигне */}
          {schedule.map(
            (
              { city, date, startDate, endDate, dealerEmail, dealerUrl },
              idx
            ) => {
              const p = CITY_POS[city];
              if (!p) return null;

              const delay = Math.min(idx * 0.08, 0.8); // леко поетапно появяване

              const label = city;
              const displayDate =
                date ??
                (startDate && endDate
                  ? `${fmtDM(startDate)}-${fmtDM(endDate)}`
                  : "");

              const padX = 8;
              const padY = 4;
              const fontSize = 14;

              const textWidth = label.length * (fontSize * 0.6);
              const rectW = textWidth + padX * 2;
              const rectH = fontSize + padY * 2;

              const bx = p.x - rectW / 2;
              const by = p.y - rectH - 4;

              return (
                <g
                  key={idx}
                  onClick={() => handleToggle(idx)}
                  style={{ cursor: "pointer" }}
                >
                  <motion.g
                    initial={{ opacity: 0 }}
                    animate={inView ? { opacity: 1 } : { opacity: 0 }}
                    transition={{ delay: inView ? delay : 0, duration: 0.25 }}
                  >
                    <circle
                      cx={p.x}
                      cy={p.y}
                      r="16"
                      fill="transparent"
                      pointerEvents="all"
                    />
                    {/* Pulse ring for current city (starts when line reaches this point) */}
                    <motion.circle
                      cx={p.x}
                      cy={p.y}
                      r="10"
                      fill="none"
                      stroke="#00B5E2"
                      strokeWidth="2"
                      initial={{ scale: 0.6, opacity: 0 }}
                      animate={{ scale: [0.6, 1.4], opacity: [0.6, 0] }}
                      transition={{
                        delay,
                        duration: 1.2,
                        repeat: Infinity,
                        ease: "easeOut",
                      }}
                    />
                    <circle
                      cx={p.x}
                      cy={p.y}
                      r="6"
                      fill="#00205B"
                      stroke="#fff"
                      strokeWidth="2"
                    />
                    <text
                      x={p.x}
                      y={by}
                      fontSize={12}
                      fill="#fff"
                      fontWeight="400"
                      textAnchor="middle"
                      alignmentBaseline="middle"
                      filter="url(#labelShadow)"
                      letterSpacing="1px"
                    >
                      {label}
                    </text>
                  </motion.g>
                </g>
              );
            }
          )}

          {selectedIdx !== null &&
            (() => {
              const sel = schedule[selectedIdx];
              const p = sel ? CITY_POS[sel.city] : null;
              if (!sel || !p) return null;
              const displayDate =
                sel.date ??
                (sel.startDate && sel.endDate
                  ? `${fmtDM(sel.startDate)}-${fmtDM(sel.endDate)}`
                  : "");
              const venueText = (
                sel.venue ||
                sel.location ||
                sel.place ||
                ""
              ).trim();

              // Base SVG viewBox dimensions
              const VBW = 800;
              const VBH = 500;
              const margin = 10;

              let popupW, popupH, px, py;
              let titleFont, dateFont, btnH, btnRadius, btnFont, btnY;
              let btn1W, btn2W, gapBtns, btn1X, btn2X, btnTextY;

              if (isMobile) {
                // MOBILE: 3/4 of map width, 2/3 height, centered.
                const MOBILE_CENTER_OFFSET_Y = 36; // compensate for parent <g transform="translate(0,-36)">
                popupW = VBW * 0.75; // 600
                popupH = VBH * (2 / 3); // ~333.33
                px = (VBW - popupW) / 2; // center X
                py = (VBH - popupH) / 2 + MOBILE_CENTER_OFFSET_Y; // center Y + compensation

                // Larger, more legible typography
                titleFont = 48;
                dateFont = 30;
                btnH = 52;
                btnRadius = 26;
                btnFont = 20;

                // Buttons near the bottom with generous padding
                btn1W = 285;
                btn2W = 265;
                gapBtns = 16;
                btnY = py + popupH - btnH - 24; // bottom padding 24
                btn1X = px + 16; // left padding 16
                btn2X = btn1X + btn1W + gapBtns;
                btnTextY = btnY + Math.round(btnH / 2);
              } else {
                // DESKTOP: keep previous compact behavior near the city
                popupW = 270;
                popupH = 160; // +20 to fit venue line nicely
                const toRight = p.x + popupW + 24 < VBW; // keep inside viewBox
                px = toRight ? p.x + 18 : p.x - popupW - 18;
                py = Math.max(
                  margin,
                  Math.min(p.y - popupH / 2, VBH - popupH - margin)
                );

                titleFont = 14;
                dateFont = 10;
                btnH = 32;
                btnRadius = 16;
                btnFont = 8;
                btnY = py + 98; // move buttons lower to clear venue

                btn1W = 130;
                btn2W = 100;
                gapBtns = 12;
                btn1X = px + 12;
                btn2X = px + 12 + btn1W + gapBtns;
                btnTextY = btnY + Math.round(btnH / 2);
              }

              const titleY = isMobile ? py + 56 : py + 24;
              const dateY = isMobile ? py + 92 : py + 44;

              // Keep equal vertical spacing: (date - title) == (venue - date)
              const gapY = dateY - titleY;
              const venueX = px + 16;
              const venueY = dateY + gapY;
              const venueW = popupW - 32; // full card content width on both mobile & desktop
              const venueH = Math.max(16, btnY - venueY - 8); // fill down to buttons

              return (
                <g>
                  {/* Click-outside overlay to close popup */}
                  <rect
                    x={0}
                    y={0}
                    width={800}
                    height={500}
                    fill="transparent"
                    pointerEvents="all"
                    onClick={() => setSelectedIdx(null)}
                  />

                  {/* Popup itself (rendered last to be on top of labels) */}
                  <g>
                    <rect
                      x={px}
                      y={py}
                      width={popupW}
                      height={popupH}
                      rx="10"
                      ry="10"
                      fill="#ffffff"
                      stroke="#E5E7EB"
                      filter="url(#popupShadow)"
                    />
                    <text
                      x={px + 16}
                      y={titleY}
                      fontSize={titleFont}
                      fontWeight="400"
                      fill="#102B4E"
                    >
                      {sel.city}
                    </text>
                    <text
                      x={px + 16}
                      y={dateY}
                      fontSize={dateFont}
                      fill="#4B5563"
                    >
                      {displayDate}
                    </text>
                    {venueText && venueW > 0 && (
                      <foreignObject
                        x={venueX}
                        y={venueY}
                        width={venueW}
                        height={venueH}
                      >
                        <div
                          xmlns="http://www.w3.org/1999/xhtml"
                          style={{
                            fontSize: dateFont, // използваме същия размер като за датите
                            lineHeight: isMobile
                              ? `${dateFont + 6}px`
                              : `${dateFont + 4}px`,
                            color: "#4B5563",
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                          }}
                        >
                          {venueText}
                        </div>
                      </foreignObject>
                    )}

                    {/* Close "X" button */}
                    <g
                      pointerEvents="all"
                      onClick={() => setSelectedIdx(null)}
                      style={{ cursor: "pointer" }}
                    >
                      <text
                        x={px + popupW - 20}
                        y={py + 20}
                        fontSize={16}
                        fontWeight="400"
                        fill="#6B7280"
                        textAnchor="middle"
                        alignmentBaseline="middle"
                      >
                        ×
                      </text>
                    </g>

                    {/* Buttons */}
                    {/* "Добави в календара" – download .ics; Cmd/Ctrl click → Google Calendar link */}
                    <g
                      pointerEvents="all"
                      onClick={(e) => {
                        e.stopPropagation();
                        let range = null;
                        if (sel.startDate && sel.endDate) {
                          range = isoToICSRange(sel.startDate, sel.endDate);
                        } else if (sel.date) {
                          range = parseDateRangeBG(sel.date, 2025);
                        }
                        if (!range) return;
                        const title = `Ford EV Roadshow – ${sel.city}`;
                        const location = venueText
                          ? `${sel.city}, ${venueText}`
                          : sel.city;
                        const details =
                          "Посетете Ford EV Roadshow и тествайте електрическите модели.";

                        const ics = buildICS({
                          title,
                          description: details,
                          location,
                          dtStart: range.dtStart,
                          dtEndExclusive: range.dtEndExclusive,
                        });
                        downloadICS(`ford-ev-roadshow-${sel.city}.ics`, ics);
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      <rect
                        x={btn1X}
                        y={btnY}
                        width={btn1W}
                        height={btnH}
                        rx={btnRadius}
                        ry={btnRadius}
                        fill="#FFFFFF"
                        stroke="#2861A4"
                      />
                      <text
                        x={btn1X + btn1W / 2}
                        y={btnTextY}
                        fontSize={btnFont}
                        textAnchor="middle"
                        alignmentBaseline="middle"
                        fontWeight="400"
                        fill="#2861A4"
                      >
                        Добави в календара
                      </text>
                    </g>
                    <g
                      pointerEvents="all"
                      onClick={(e) => {
                        e.stopPropagation();
                        openContact(sel.city);
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      <rect
                        x={btn2X}
                        y={btnY}
                        width={btn2W}
                        height={btnH}
                        rx={btnRadius}
                        ry={btnRadius}
                        fill="#00205B"
                      />
                      <text
                        x={btn2X + btn2W / 2}
                        y={btnTextY}
                        fontSize={btnFont}
                        textAnchor="middle"
                        alignmentBaseline="middle"
                        fontWeight="400"
                        fill="#FFFFFF"
                        className="select-none"
                      >
                        Свържи се с дилър
                      </text>
                    </g>
                  </g>
                </g>
              );
            })()}
        </g>
      </svg>
    </div>
  );
}
